import React, { useState } from "react";
import {
    Settings,
    AlertTriangle,
    Mail,
    Download,
    RefreshCw,
    Lock,
    Unlock,
    ShieldCheck,
    Smartphone,
    WifiOff,
    Link2,
    FlaskConical,
    GitBranch,
    ExternalLink,
} from "lucide-react";

const GITHUB_APP_SLUG = "agentui-ai";
import { useTranslation } from "react-i18next";
import { Switch } from "@shadcn-in-nx/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@shadcn-in-nx/ui/alert";
import { useAuth } from "../../context/AuthContext";
import PlanRestrictionAlert from "./PlanRestrictionAlert";
import GitHubRepoSelect from "./GitHubRepoSelect";

function SettingsPanel({
    isFunctionAPIEnabled,
    onFunctionAPIToggle,
    isUpdatingSettings,
    onExportProject,
    hasBackendFunctionsAccess = true, // Default to true for backwards compatibility
    hasExportAccess = true, // Default to true for backwards compatibility
    isProductionLocked = false, // Production lock state
    onProductionLockToggle, // Callback for production lock toggle
    isInstallableWebApp = false, // Installable web app state
    onInstallableWebAppToggle, // Callback for installable web app toggle
    isOfflineEnabled = false, // Offline mode state
    onOfflineToggle, // Callback for offline mode toggle
    isDeepLinkEnabled = false, // Deep linking state
    onDeepLinkToggle, // Callback for deep link toggle
    isRegressionTestingEnabled = false,
    onRegressionTestingToggle,
    gitHubRepo = "", // GitHub repo URL
    onGitHubSettingsChange, // Callback for repo changes
    isGitHubConnected = false, // Whether GitHub OAuth is connected
    onGitHubConnect, // Callback to start GitHub OAuth
    workflowId, // Workflow ID for fetching GitHub repos
}) {
    const { t } = useTranslation();
    const { isFeatureEnabled, isInTrial } = useAuth();
    const [isExporting, setIsExporting] = useState(false);
    const [isTogglingLock, setIsTogglingLock] = useState(false);

    const handleLockToggle = async () => {
        if (!isFeatureEnabled("productionLock")) {
            return;
        }
        if (onProductionLockToggle) {
            setIsTogglingLock(true);
            try {
                await onProductionLockToggle(!isProductionLocked);
            } finally {
                setIsTogglingLock(false);
            }
        }
    };

    const handleExportClick = async () => {
        if (!hasExportAccess) {
            return;
        }
        if (onExportProject) {
            setIsExporting(true);
            try {
                await onExportProject();
            } finally {
                setIsExporting(false);
            }
        }
    };

    return (
        <div className="w-full h-full overflow-auto bg-gray-50 p-4 md:p-6 pb-8">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Settings className="w-5 h-5 text-gray-600" />
                        {t("appPreview.settings") || "Settings"}
                    </h2>

                    {/* Production Lock Toggle */}
                    {onProductionLockToggle && (
                        <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg mb-4">
                            <div className="flex-1 mr-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <h3 className="text-sm font-medium text-gray-900">
                                        {t("appPreview.productionLock") ||
                                            "Production Lock"}
                                    </h3>
                                    {isProductionLocked ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                            <Lock className="w-3 h-3 mr-1" />
                                            {t("appPreview.locked") || "Locked"}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                            <Unlock className="w-3 h-3 mr-1" />
                                            {t("appPreview.unlocked") ||
                                                "Unlocked"}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-500">
                                    {t(
                                        "appPreview.productionLockDescription"
                                    ) ||
                                        "When locked, prevents any changes to the production environment. Development and staging environments can still be modified."}
                                </p>

                                {/* Plan Restriction Alert */}
                                {!isFeatureEnabled("productionLock") && (
                                    <PlanRestrictionAlert
                                        titleKey="appPreview.upgradeRequired"
                                        titleFallback="Upgrade Required"
                                        descriptionKey="appPreview.productionLockUpgradeMessage"
                                        descriptionFallback="Production lock is available on Creator plan and above. Upgrade your plan to enable this feature."
                                        variant="inline"
                                    />
                                )}

                                {isProductionLocked && (
                                    <Alert className="mt-4 bg-orange-50 border-orange-200">
                                        <ShieldCheck className="h-4 w-4 text-orange-600" />
                                        <AlertTitle className="text-orange-800">
                                            {t(
                                                "appPreview.productionProtected"
                                            ) || "Production Protected"}
                                        </AlertTitle>
                                        <AlertDescription className="text-orange-700">
                                            {t(
                                                "appPreview.productionProtectedMessage"
                                            ) ||
                                                "Your production environment is protected from accidental changes. Unlock to make modifications."}
                                        </AlertDescription>
                                    </Alert>
                                )}
                            </div>
                            <Switch
                                checked={isProductionLocked}
                                onCheckedChange={handleLockToggle}
                                disabled={
                                    isTogglingLock ||
                                    isUpdatingSettings ||
                                    !isFeatureEnabled("productionLock")
                                }
                            />
                        </div>
                    )}

                    {/* Export Project */}
                    {onExportProject && (
                        <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg mb-4">
                            <div className="flex-1 mr-4">
                                <h3 className="text-sm font-medium text-gray-900 mb-1">
                                    {t("appPreview.exportProject") ||
                                        "Export Project"}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    {t("appPreview.exportDescription") ||
                                        "Download your project source code as a ZIP file."}
                                </p>

                                {/* Plan Restriction Alert */}
                                {!hasExportAccess && (
                                    <PlanRestrictionAlert
                                        titleKey="appPreview.upgradeRequired"
                                        titleFallback="Upgrade Required"
                                        descriptionKey="appPreview.exportUpgradeMessage"
                                        descriptionFallback="Project export is available on Creator plan and above. Upgrade your plan to export your project source code."
                                        variant="inline"
                                    />
                                )}
                            </div>
                            <button
                                onClick={handleExportClick}
                                disabled={isExporting || !hasExportAccess}
                                className="flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isExporting ? (
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Download className="w-4 h-4 mr-2" />
                                )}
                                {isExporting
                                    ? t("appPreview.exporting") ||
                                      "Exporting..."
                                    : t("appPreview.export") || "Export"}
                            </button>
                        </div>
                    )}

                    {/* Installable Web App Toggle */}
                    {onInstallableWebAppToggle && (
                        <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg mb-4">
                            <div className="flex-1 mr-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <Smartphone className="w-4 h-4 text-gray-600" />
                                    <h3 className="text-sm font-medium text-gray-900">
                                        {t("appPreview.installableWebApp") ||
                                            "Installable Web App"}
                                    </h3>
                                </div>
                                <p className="text-sm text-gray-500">
                                    {t(
                                        "appPreview.installableWebAppDescription"
                                    ) ||
                                        "Enable this to make your app installable as a Progressive Web App (PWA). Users can install it on their devices for a native app-like experience."}
                                </p>
                            </div>
                            <Switch
                                checked={isInstallableWebApp}
                                onCheckedChange={onInstallableWebAppToggle}
                                disabled={isUpdatingSettings}
                            />
                        </div>
                    )}

                    {/* Offline Mode Toggle - Only shown when PWA is enabled */}
                    {onOfflineToggle && isInstallableWebApp && (
                        <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg mb-4">
                            <div className="flex-1 mr-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <WifiOff className="w-4 h-4 text-gray-600" />
                                    <h3 className="text-sm font-medium text-gray-900">
                                        {t("appPreview.offlineMode") ||
                                            "Offline Mode"}
                                    </h3>
                                </div>
                                <p className="text-sm text-gray-500">
                                    {t("appPreview.offlineModeDescription") ||
                                        "Cache app files for offline use. When enabled, users can access the app even without an internet connection. Note: Data is managed by React Query and will sync when back online."}
                                </p>
                            </div>
                            <Switch
                                checked={isOfflineEnabled}
                                onCheckedChange={onOfflineToggle}
                                disabled={isUpdatingSettings}
                            />
                        </div>
                    )}

                    {/* Deep Linking Toggle */}
                    {onDeepLinkToggle && (
                        <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg mb-4">
                            <div className="flex-1 mr-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <Link2 className="w-4 h-4 text-gray-600" />
                                    <h3 className="text-sm font-medium text-gray-900">
                                        {t("appPreview.deepLinking") ||
                                            "Deep Linking"}
                                    </h3>
                                </div>
                                <p className="text-sm text-gray-500">
                                    {t(
                                        "appPreview.deepLinkingDescription"
                                    ) ||
                                        "Enable cross-app navigation. When enabled, you can link pages in this app to pages in other apps using @Linked Apps in the chat."}
                                </p>

                                {!isFeatureEnabled("deepLinking") && (
                                    <PlanRestrictionAlert
                                        titleKey="appPreview.upgradeRequired"
                                        titleFallback="Upgrade Required"
                                        descriptionKey="appPreview.deepLinkingUpgradeMessage"
                                        descriptionFallback="Deep linking is available on Innovator plan and above. Upgrade your plan to enable cross-app navigation."
                                        variant="inline"
                                    />
                                )}
                            </div>
                            <Switch
                                checked={isDeepLinkEnabled}
                                onCheckedChange={onDeepLinkToggle}
                                disabled={
                                    isUpdatingSettings ||
                                    !isFeatureEnabled("deepLinking")
                                }
                            />
                        </div>
                    )}

                    {/* GitHub Integration */}
                    {onGitHubConnect && (
                        <div className="flex items-start p-4 bg-gray-50 rounded-lg mb-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <GitBranch className="w-4 h-4 text-gray-600" />
                                    <h3 className="text-sm font-medium text-gray-900">
                                        {t("appPreview.gitHubIntegration") ||
                                            "GitHub Integration"}
                                    </h3>
                                </div>
                                <p className="text-sm text-gray-500 mb-3">
                                    {t(
                                        "appPreview.gitHubIntegrationDescription"
                                    ) ||
                                        "Enable two-way GitHub sync. Push your app code to a repository or pull changes from it."}
                                </p>

                                {isInTrial && (
                                    <PlanRestrictionAlert
                                        titleKey="appPreview.upgradeRequired"
                                        titleFallback="Upgrade Required"
                                        descriptionKey="appPreview.gitHubTrialRestrictionMessage"
                                        descriptionFallback="GitHub integration is not available during your trial. Upgrade to a paid plan to enable GitHub sync."
                                        variant="inline"
                                    />
                                )}

                                {!isGitHubConnected ? (
                                    <button
                                        onClick={onGitHubConnect}
                                        disabled={isUpdatingSettings || isInTrial}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <GitBranch className="w-4 h-4" />
                                        {t("appPreview.gitHubConnectAccount") ||
                                            "Connect GitHub Account"}
                                    </button>
                                ) : (
                                    <div className="space-y-2">
                                        <GitHubRepoSelect
                                            workflowId={workflowId}
                                            value={gitHubRepo}
                                            onChange={onGitHubSettingsChange}
                                        />
                                        <p className="text-xs text-gray-400">
                                            {t("appPreview.gitHubBranchHint")}
                                        </p>
                                        <div className="flex items-center gap-3 text-xs pt-1">
                                            {gitHubRepo && (
                                                <a
                                                    href={`https://github.com/${gitHubRepo}`}
                                                    target="_blank"
                                                    rel="noreferrer noopener"
                                                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
                                                >
                                                    {t("appPreview.gitHubViewRepo") || "View repository"}
                                                    <ExternalLink className="w-3 h-3" />
                                                </a>
                                            )}
                                            <a
                                                href={`https://github.com/apps/${GITHUB_APP_SLUG}/installations/new`}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
                                            >
                                                {t("appPreview.gitHubManageRepoAccess") || "Manage repo access"}
                                                <ExternalLink className="w-3 h-3" />
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Function API Toggle */}
                    <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1 mr-4">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-sm font-medium text-gray-900">
                                    {t("appPreview.enableFunctionAPI") ||
                                        "Enable Function API"}
                                </h3>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    {t("common.beta") || "Beta"}
                                </span>
                            </div>
                            <p className="text-sm text-gray-500">
                                {t("appPreview.functionAPIDescription") ||
                                    "Enable the Function API to allow your app to execute backend functions."}
                            </p>

                            {/* Plan Restriction Alert */}
                            {!hasBackendFunctionsAccess && (
                                <PlanRestrictionAlert
                                    titleKey="appPreview.upgradeRequired"
                                    titleFallback="Upgrade Required"
                                    descriptionKey="appPreview.backendFunctionsUpgradeMessage"
                                    descriptionFallback="Backend functions are available on Creator plan and above. Upgrade your plan to enable this feature."
                                    variant="inline"
                                />
                            )}

                            {isFunctionAPIEnabled &&
                                hasBackendFunctionsAccess && (
                                    <div className="mt-4 space-y-3">
                                        <Alert
                                            variant="warning"
                                            className="bg-yellow-50 border-yellow-200"
                                        >
                                            <AlertTriangle className="h-4 w-4 text-yellow-600" />
                                            <AlertTitle className="text-yellow-800">
                                                {t("common.betaWarning") ||
                                                    "Beta Feature"}
                                            </AlertTitle>
                                            <AlertDescription className="text-yellow-700">
                                                {t(
                                                    "appPreview.functionAPIWarning"
                                                ) ||
                                                    "This feature is in beta and might contain errors."}
                                            </AlertDescription>
                                        </Alert>

                                        <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 p-3 rounded border border-blue-100">
                                            <Mail className="w-4 h-4 text-blue-500" />
                                            <span>
                                                {t("appPreview.reportErrors") ||
                                                    "If you find any errors, send us an email to"}{" "}
                                                <a
                                                    href="mailto:support@agentui.ai"
                                                    className="text-blue-600 hover:underline font-medium"
                                                >
                                                    support@agentui.ai
                                                </a>
                                            </span>
                                        </div>
                                    </div>
                                )}
                        </div>
                        <Switch
                            checked={isFunctionAPIEnabled}
                            onCheckedChange={onFunctionAPIToggle}
                            disabled={
                                isUpdatingSettings || !hasBackendFunctionsAccess
                            }
                        />
                    </div>

                    {/* AI Testing Toggle */}
                    {onRegressionTestingToggle && (
                        <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg mt-4">
                            <div className="flex-1 mr-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <FlaskConical className="w-4 h-4 text-gray-600" />
                                    <h3 className="text-sm font-medium text-gray-900">
                                        {t("testing.aiTesting") || "AI Testing"}
                                    </h3>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                        Beta
                                    </span>
                                </div>
                                <p className="text-sm text-gray-500">
                                    {t("testing.aiTestingDescription") ||
                                        "Enable AI-powered testing for your app. When enabled, the AI will automatically create and run browser-based tests for your pages to catch issues early."}
                                </p>

                                {!isFeatureEnabled("aiTesting") && (
                                    <PlanRestrictionAlert
                                        titleKey="appPreview.upgradeRequired"
                                        titleFallback="Upgrade Required"
                                        descriptionKey="testing.aiTestingUpgradeMessage"
                                        descriptionFallback="AI Testing is available on the Visionary plan ($250/mo) and above. Upgrade your plan to enable automated testing."
                                        variant="inline"
                                    />
                                )}
                            </div>
                            <Switch
                                checked={isRegressionTestingEnabled}
                                onCheckedChange={onRegressionTestingToggle}
                                disabled={
                                    isUpdatingSettings ||
                                    !isFeatureEnabled("aiTesting")
                                }
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SettingsPanel;
